import { UserPopoverItem } from "@frontends-v3/design-system";

import { HelpCenterLinks } from "../models/help/help-center-links.interface";
import { featureAvailability, menuItemAvailability } from "../offline/offline-policy.util";

/**
 * `offline`: presença do item "Modo Offline" (DEV-966). `null`/ausente = app sem a feature (o item
 * não aparece). Com o modo LIGADO, os itens da Central de Ajuda saem do menu (CA 09) e "Minha
 * conta" fica indisponível — só Atribuições é alcançável (CA 05).
 */
const OFFLINE_ITEM_REASON = "Indisponível enquanto o Modo Offline estiver ligado.";

export function buildUserPopoverItems(
  helpLinks: HelpCenterLinks,
  options: { hasError: boolean; offline?: { enabled: boolean } | null }
): UserPopoverItem[] {
  const offlineEnabled = options.offline?.enabled === true;
  // DEV-966 (CA 09): com o modo ligado a Central de Ajuda fica visível, mas indisponível.
  const helpPaused = featureAvailability("help", offlineEnabled) !== "allowed";
  const helpDisabled = (url: string | null): boolean => helpPaused || options.hasError || !url;
  const offlineReason = (paused: boolean): string | undefined =>
    paused ? OFFLINE_ITEM_REASON : undefined;

  const offlineItem: UserPopoverItem[] = options.offline
    ? [
        {
          id: "offline-mode",
          title: "Modo Offline",
          description: "Ver suas atribuições em sala sem precisar de internet",
          icon: "uil uil-wifi",
          control: "toggle",
          checked: offlineEnabled
        }
      ]
    : [];

  const helpItems: UserPopoverItem[] = [
    {
      id: "faq",
      title: "Dúvidas Frequentes",
      description: "Acesse nosso FAQ e Manual de Navegação",
      icon: "uil uil-question-circle",
      externalUrl: helpLinks.faq ?? undefined,
      disabled: helpDisabled(helpLinks.faq),
      disabledReason: offlineReason(helpPaused)
    },
    {
      id: "specialist",
      title: "Especialista Lekto",
      description: "Fale com um Especialista Lekto pelo WhatsApp",
      icon: "uil uil-whatsapp",
      externalUrl: helpLinks.specialist ?? undefined,
      disabled: helpDisabled(helpLinks.specialist),
      disabledReason: offlineReason(helpPaused)
    },
    {
      id: "support",
      title: "Abrir um chamado",
      description: "Envie uma solicitação ou sugestão",
      icon: "uil uil-exclamation-circle",
      externalUrl: helpLinks.support ?? undefined,
      disabled: helpDisabled(helpLinks.support),
      disabledReason: offlineReason(helpPaused)
    }
  ];

  // Protótipo: "Modo Offline" é o primeiro item do menu.
  return [
    ...offlineItem,
    {
      id: "my-account",
      title: "Minha conta",
      description: "Administrar minha conta e editar preferências",
      icon: "uil uil-user",
      route: "/my-account",
      disabled: menuItemAvailability("/my-account", offlineEnabled) !== "allowed",
      disabledReason: offlineReason(
        menuItemAvailability("/my-account", offlineEnabled) !== "allowed"
      )
    },
    ...helpItems,
    {
      id: "logout",
      title: "Sair",
      description: "Faça logout com segurança",
      // `uil-signout` desenha a seta entrando na caixa; no Unicons 4.2 o glifo de linha mais
      // próximo do protótipo (seta saindo para a direita) é o `arrow-from-right`.
      icon: "uil uil-arrow-from-right",
      showArrow: false
    }
  ];
}
